import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { TaskEngine, IDLE_TASK_STATE, type TaskRunState } from './engine/TaskEngine';
import { TourEngine, type TourResumeToken, type TourRunState } from './engine/TourEngine';
import {
  type TourProgressStore,
  createLocalProgressStore,
  createMemoryProgressStore,
} from './engine/progressStore';
import { validateSandboxLinks } from './sandboxLinks';
import { getTaskForStep, validateTasks } from './tasks';
import type { TaskFlow } from './tasks/types';
import {
  TOURS,
  computeStageProgress,
  validateTours,
  type Tour,
  type TourProgressView,
} from './tours';

export interface TourGuideApi {
  tours: Tour[];
  run: TourRunState;
  progress: Record<string, boolean>;
  stageProgress: TourProgressView;
  start(tourId: string): void;
  stop(): void;
  goToStep(stepId: string): boolean;
  next(): boolean;
  previous(): boolean;

  task: TaskRunState;
  taskFor(stepId: string | null | undefined): { id: string; cta: string } | null;
  startTask(stepId: string): boolean;
  taskNext(): void;
  taskPrevious(): void;
  taskSkip(): void;
  taskSkipModule(): void;
  taskSkipSegment(): void;
  endTask(exit?: TaskExit | boolean): void;
}

export type TaskExit = 'stay' | 'step' | 'module' | 'back' | 'close';

const AFTER_END_TIMEOUT_MS = 4000;

const MODAL_LAYER_SELECTOR = '[role="dialog"]:not(#driver-popover-content), [role="alertdialog"]';

const DRAWER_OVERLAY_SELECTOR = '[data-floating-drawer-overlay]';

function hasOpenModalLayer(): boolean {
  return Array.from(document.querySelectorAll<HTMLElement>(MODAL_LAYER_SELECTOR)).some((el) => {
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return false;
    const style = window.getComputedStyle(el);
    return style.visibility !== 'hidden' && style.display !== 'none';
  });
}

export function restoreBackground(): void {
  if (hasOpenModalLayer()) return;

  document
    .querySelectorAll<HTMLElement>(DRAWER_OVERLAY_SELECTOR)
    .forEach((overlay) => overlay.remove());

  if (document.body.style.pointerEvents === 'none') {
    document.body.style.removeProperty('pointer-events');
  }
}

function settleFlowExit(flow: TaskFlow | null): Promise<void> {
  if (!flow?.afterEnd) return Promise.resolve();

  const cleanup = Promise.resolve()
    .then(() => flow.afterEnd?.())
    .catch((error) => {
      console.warn(`[tour-guide] task "${flow.id}": afterEnd failed`, error);
    });

  return Promise.race([
    cleanup,
    new Promise<void>((resolve) => {
      window.setTimeout(resolve, AFTER_END_TIMEOUT_MS);
    }),
  ]).then(() => undefined);
}

function settleFlowExitAndBackground(flow: TaskFlow | null): Promise<void> {
  return settleFlowExit(flow).then(() => {
    restoreBackground();
  });
}

const IDLE_PROGRESS: TourProgressView = { stages: [], activeStage: 0, totalStages: 0 };

const Ctx = createContext<TourGuideApi | null>(null);

export function useTourGuide(): TourGuideApi | null {
  return useContext(Ctx);
}

export interface TourGuideProviderProps {
  children?: React.ReactNode;
  enabled?: boolean;
  persist?: boolean;
  progressStore?: TourProgressStore;
  tasks?: boolean;
  tours?: Tour[];
}

export function TourGuideProvider({
  children,
  enabled = true,
  persist = false,
  progressStore,
  tasks = false,
  tours = TOURS,
}: TourGuideProviderProps) {
  const store = useMemo<TourProgressStore>(
    () => progressStore ?? (persist ? createLocalProgressStore() : createMemoryProgressStore()),
    [progressStore, persist],
  );

  const [progress, setProgress] = useState<Record<string, boolean>>(() => store.read());
  const [run, setRun] = useState<TourRunState>({
    running: false,
    tourId: null,
    stepId: null,
    index: -1,
    total: 0,
    order: [],
  });

  const [task, setTask] = useState<TaskRunState>(IDLE_TASK_STATE);

  const engineRef = useRef<TourEngine | null>(null);
  const taskEngineRef = useRef<TaskEngine | null>(null);

  const resumeTokenRef = useRef<TourResumeToken | null>(null);
  const activeFlowRef = useRef<TaskFlow | null>(null);

  const tasksEnabledRef = useRef(tasks);
  tasksEnabledRef.current = tasks;
  const startTaskRef = useRef<(stepId: string) => void>(() => {});
  const endTaskRef = useRef<(exit?: TaskExit | boolean) => void>(() => {});

  if (!engineRef.current) {
    engineRef.current = new TourEngine({
      onState: setRun,
      onComplete: (tourId) => {
        setProgress((prev) => {
          if (prev[tourId]) return prev;
          const next = { ...prev, [tourId]: true };
          store.write(next);
          return next;
        });
      },
      taskOffer: (stepId) => {
        if (!tasksEnabledRef.current) return null;
        const flow = getTaskForStep(stepId);
        return flow ? { label: flow.cta } : null;
      },
      onTaskStart: (stepId) => startTaskRef.current(stepId),
    });
  }

  if (!taskEngineRef.current) {
    taskEngineRef.current = new TaskEngine({
      onState: setTask,
      onFinish: () => {
        const flow = activeFlowRef.current;
        const settled = settleFlowExitAndBackground(flow);
        const whenDone = flow?.whenDone ?? 'panel';
        if (whenDone === 'panel') return;
        void settled.then(() => endTaskRef.current(whenDone === 'close' ? 'close' : 'step'));
      },
    });
  }

  const resolveTour = useCallback(
    (id: string): Tour | undefined => tours.find((t) => t.id === id),
    [tours],
  );

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const problems = [...validateTours(tours), ...validateSandboxLinks(), ...validateTasks()];
    if (problems.length) console.warn('[tour-guide] schema problems:\n - ' + problems.join('\n - '));
  }, [tours]);

  useEffect(() => () => engineRef.current?.destroy(), []);

  const start = useCallback((tourId: string) => {
    const tour = resolveTour(tourId);
    if (!tour) {
      console.warn(`[tour-guide] no tour registered with id "${tourId}"`);
      return;
    }
    void engineRef.current?.open(tour).then((result) => {
      if (import.meta.env.DEV && result.started && result.skipped.length) {
        console.info(
          `[tour-guide] "${tour.label}": ${result.resolved.length} steps, ` +
            `skipped ${result.skipped.length} (${result.skipped.join(', ')})`,
        );
      }
    });
  }, [resolveTour]);

  const stop = useCallback(() => {
    taskEngineRef.current?.stop();
    resumeTokenRef.current = null;
    activeFlowRef.current = null;
    engineRef.current?.stop();
  }, []);

  const goToStep = useCallback((stepId: string) => engineRef.current?.goToStep(stepId) ?? false, []);

  const next = useCallback(() => engineRef.current?.next() ?? false, []);

  const previous = useCallback(() => engineRef.current?.previous() ?? false, []);

  const taskFor = useCallback((stepId: string | null | undefined) => {
    if (!tasksEnabledRef.current) return null;
    const flow = getTaskForStep(stepId);
    return flow ? { id: flow.id, cta: flow.cta } : null;
  }, []);

  const startTask = useCallback((stepId: string) => {
    if (!tasksEnabledRef.current) return false;
    const flow = getTaskForStep(stepId);
    if (!flow) return false;

    resumeTokenRef.current = engineRef.current?.pauseForTask(stepId) ?? null;
    activeFlowRef.current = flow;
    return taskEngineRef.current?.start(flow) ?? false;
  }, []);

  const taskNext = useCallback(() => taskEngineRef.current?.next(), []);
  const taskSkip = useCallback(() => taskEngineRef.current?.skip(), []);

  const endTask = useCallback((exit: TaskExit | boolean = 'stay') => {
    const landing: TaskExit = exit === true ? 'step' : exit === false ? 'stay' : exit;

    const flow = activeFlowRef.current;
    activeFlowRef.current = null;
    taskEngineRef.current?.stop();
    const token = resumeTokenRef.current;
    resumeTokenRef.current = null;

    const settled = settleFlowExitAndBackground(flow);

    if (landing === 'close') {
      void settled.then(() => engineRef.current?.stop());
      return;
    }

    if (!token) return;

    const tour = resolveTour(token.tourId);
    if (!tour) return;

    void settled.then(() => engineRef.current?.resumeFrom(token, tour)).then((landed) => {
      if (!landed) {
        if (import.meta.env.DEV) {
          console.info(
            `[tour-guide] could not resume "${token.tourId}" at step "${token.stepId}" — ` +
              `it is not in the run any more.`,
          );
        }
        return;
      }
      if (landing === 'step') engineRef.current?.next();
      else if (landing === 'module') engineRef.current?.nextModule(token.stepId);
      else if (landing === 'back') engineRef.current?.previousFrom(token.stepId);
    });
  }, [resolveTour]);

  endTaskRef.current = endTask;

  const taskPrevious = useCallback(() => {
    if (taskEngineRef.current?.previous()) return;
    endTask('back');
  }, [endTask]);

  const taskSkipModule = useCallback(() => endTask('module'), [endTask]);

  useEffect(() => () => taskEngineRef.current?.destroy(), []);

  const stageProgress = useMemo<TourProgressView>(() => {
    if (!run.running || !run.tourId) return IDLE_PROGRESS;
    const tour = resolveTour(run.tourId);
    if (!tour) return IDLE_PROGRESS;
    return computeStageProgress(tour, run.order, run.stepId);
  }, [run, resolveTour]);

  const taskSkipSegment = useCallback(() => {
    const handled = taskEngineRef.current?.skipSegment() ?? true;
    if (!handled) endTask('module');
  }, [endTask]);

  startTaskRef.current = startTask;

  const value = useMemo<TourGuideApi>(
    () => ({
      tours,
      run,
      progress,
      stageProgress,
      start,
      stop,
      goToStep,
      next,
      previous,
      task,
      taskFor,
      startTask,
      taskNext,
      taskPrevious,
      taskSkip,
      taskSkipModule,
      taskSkipSegment,
      endTask,
    }),
    [
      tours,
      run,
      progress,
      stageProgress,
      start,
      stop,
      goToStep,
      next,
      previous,
      task,
      taskFor,
      startTask,
      taskNext,
      taskPrevious,
      taskSkip,
      taskSkipModule,
      taskSkipSegment,
      endTask,
    ],
  );

  if (!enabled) return <>{children}</>;

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
